import React from 'react'

import { Button } from '@acorns/web-components'
import { compose, withHandlers, withState } from 'recompose'
import styled from 'styled-components'

import CloseSVG from 'assets/icons/close-black.svg'
import MagnifyingGlassSVG from 'assets/icons/magnifying-glass.svg'
import {
  SEARCH_BOX_CLEAR,
  SEARCH_BOX_CTA,
  SEARCH_BOX_TERM,
  SEARCH_BOX_TRIGGER,
} from 'utils/testing/testIDs'

import { theme } from '../../theme'

type Props = {
  placeholder?: string
  onSearch: (search: { searchTerm: string }) => void
}

type StateProps = {
  searchTerm: string
  setSearchTerm: (val: string) => void
}

type HandlerProps = {
  handleClear: () => void
  handleChange: (event: any) => void
  handleSearch: () => void
  handleKeyPress: (event: any) => void
}

type InnerProps = Props & HandlerProps & StateProps

const Wrapper = styled.div`
  display: flex;
`

const Container = styled.div`
  background-color: white;
  border: solid 1px ${theme('colors.stoneLight')};
  border-radius: 18px;
  height: 36px;
  width: 195px;
  min-width: 195px;
  display: flex;
  flex: 1;

  @media (max-width: ${theme('breakpoints.md')}px) {
    width: 100%;
  }
`

const SearchTrigger = styled.img`
  width: 16px;
  height: 34px;
  margin: 0 12px;
`

const SearchClear = styled('img')<{ visible: boolean }>`
  width: 32px;
  height: 34px;
  padding: 11px;
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
`

const SearchInput = styled.input<{ tabindex?: string }>`
  border: none;
  flex: 1;
  color: ${theme('colors.slate')};
  line-height: 19px;
  font-size: 14px;
  font-weight: 400;
  width: 100%;
`

const SearchButton = styled(Button)`
  &&& {
    height: 36px;
    margin-left: 10px;
    flex: 0;
    min-width: 95px;
  }
`

const enhance = compose<InnerProps, Props>(
  withState('searchTerm', 'setSearchTerm', ''),
  withHandlers<Props & StateProps, HandlerProps>({
    handleClear:
      ({ setSearchTerm }) =>
      () => {
        setSearchTerm('')
      },
    handleChange:
      ({ setSearchTerm }) =>
      (event) => {
        // Default ascii chars only. This is to prevent users from entering in emojis.
        // eslint-disable-next-line no-control-regex
        setSearchTerm(event.target.value.replace(/[^\x00-\x7F]/g, ''))
      },
    handleSearch:
      ({ onSearch, searchTerm }) =>
      () => {
        return searchTerm && onSearch({ searchTerm })
      },
    handleKeyPress:
      ({ onSearch, searchTerm }) =>
      ({ key }) => {
        return key === 'Enter' && searchTerm && onSearch({ searchTerm })
      },
  }),
)

export default enhance((props: InnerProps) => (
  <Wrapper>
    <Container>
      <SearchTrigger
        onClick={props.handleSearch}
        src={MagnifyingGlassSVG}
        data-testid={SEARCH_BOX_TRIGGER}
      />
      <SearchInput
        value={props.searchTerm}
        placeholder={props.placeholder || 'Search'}
        onChange={props.handleChange}
        onKeyPress={props.handleKeyPress}
        type="search"
        tabindex="-1"
        data-testid={SEARCH_BOX_TERM}
      />
      <SearchClear
        onClick={props.handleClear}
        src={CloseSVG}
        visible={props.searchTerm.length > 0}
        data-testid={SEARCH_BOX_CLEAR}
      />
    </Container>
    <SearchButton
      onPress={props.handleSearch}
      disabled={props.searchTerm.length === 0}
      data-testid={SEARCH_BOX_CTA}
    >
      Search
    </SearchButton>
  </Wrapper>
))
